<template>
  <div class="bg-grey-100 flex min-h-screen w-full justify-center p-6">
    <div class="flex w-full max-w-[400px] flex-col px-[30px] py-12">
      <header
        class="font-soehne mb-10 flex flex-col gap-2 text-[28px] leading-8 font-medium"
      >
        <h1 class="text-grey-700">{{ t('PUBLIC_WELCOME') }}</h1>
        <p
          class="bg-[linear-gradient(90deg,#141B4D_0%,#AE2573_51.69%,#FF6F6A_89.03%,#F8E08E_116.02%)] bg-clip-text text-transparent [-webkit-background-clip:text]"
        >
          {{ t('PUBLIC_SIGN_IN_TO_YOUR_ACCOUNT') }}
        </p>
      </header>

      <TextFieldRoot class="mb-3" :invalid="!!urlError">
        <TextFieldBox>
          <TextFieldContent>
            <TextFieldLabel>{{ t('COMPANY_URL') }}</TextFieldLabel>
            <TextFieldInput
              v-model="workspaceUrl"
              type="text"
              autocomplete="username"
              :placeholder="t('COMPANY_URL_JIGSAWCREATE_COM')"
              @update:model-value="clearUrlError"
              @keyup.enter="handleNext"
            />
          </TextFieldContent>
        </TextFieldBox>
        <TextFieldHint v-if="urlError">{{ urlError }}</TextFieldHint>
      </TextFieldRoot>

      <Button :disabled="isSubmitting" @click="handleNext">
        {{ t('NEXT') }}
      </Button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  officeAuthStartUrlErrorMessage,
  persistWorkspaceOriginFromAuthStartUrl,
  readStoredWorkspaceHostname,
  resolveOfficeAuthStartUrl,
  validateWorkspaceBySetupRequest,
} from '@/utils';
import { OFFICE_DIALOG_PARENT_ORIGIN_PARAM } from '@/common/constants';
import {
  Button,
  TextFieldBox,
  TextFieldContent,
  TextFieldHint,
  TextFieldInput,
  TextFieldLabel,
  TextFieldRoot,
} from '@jigsaw/ui';

const { t } = useI18n();

const workspaceUrl = ref('');
const urlError = ref('');
const isSubmitting = ref(false);

onMounted(() => {
  const stored = readStoredWorkspaceHostname();
  if (stored) {
    workspaceUrl.value = stored;
  }
});

function clearUrlError(): void {
  urlError.value = '';
}

async function handleNext(): Promise<void> {
  const result = resolveOfficeAuthStartUrl(workspaceUrl.value);
  if (typeof result !== 'string') {
    urlError.value = officeAuthStartUrlErrorMessage(result.error);
    return;
  }
  const origin = new URL(result).origin;
  isSubmitting.value = true;
  try {
    const check = await validateWorkspaceBySetupRequest(
      origin,
      workspaceUrl.value
    );
    if (!check.ok) {
      urlError.value = officeAuthStartUrlErrorMessage(check.error);
      return;
    }
    persistWorkspaceOriginFromAuthStartUrl(result);
    const authStartUrl = new URL(result);
    authStartUrl.searchParams.set(
      OFFICE_DIALOG_PARENT_ORIGIN_PARAM,
      window.location.origin
    );
    window.location.href = authStartUrl.href;
  } finally {
    isSubmitting.value = false;
  }
}
</script>
